'use client';
import { useArticles } from '@/hooks/useArticles';
import React, { useEffect, useState } from 'react';
import { Article } from '@/types/data';
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Link,
  Send,
  Smile,
  Gift,
  Sticker,
  Camera,
  Paperclip,
  MessageCircle,
} from 'lucide-react';
import ArticleMetadata from '@/components/common/ArticleMetadata';
import socialSharing from '@/utils/socialSharing';
import Toast from '@/components/ui/Toast';
import Image from 'next/image';
import ContactForm from '@/modules/lien-he/components/ContactForm';

interface ArticlePageProps {
  params: {
    slug: string;
  };
}

const ArticlePage: React.FC<ArticlePageProps> = ({ params }) => {
  const { articles, loading } = useArticles();
  const { slug } = params;
  const [article, setArticle] = useState<Article | null>(null);
  const [comment, setComment] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  useEffect(() => {
    if (articles.length > 0) {
      const foundArticle = articles.find(article => article.slug === slug);
      setArticle(foundArticle || null);
    }
  }, [articles, slug]);

  // Social sharing functions
  const getCurrentUrl = () => {
    return socialSharing.getCurrentUrl() || `https://www.khoahoconline-ome.edu.vn/tin-tuc/${slug}`;
  };

  const shareOnFacebook = () => {
    if (!article) return;
    socialSharing.facebook({
      url: getCurrentUrl(),
      title: article.title,
      description: article.content.substring(0, 160),
    });
  };

  const shareOnTwitter = () => {
    if (!article) return;
    socialSharing.twitter({
      url: getCurrentUrl(),
      title: article.title,
      description: article.content.substring(0, 160),
    });
  };

  const shareOnLinkedIn = () => {
    if (!article) return;
    socialSharing.linkedin({
      url: getCurrentUrl(),
      title: article.title,
      description: article.content.substring(0, 160),
    });
  };

  const copyToClipboard = async () => {
    const success = await socialSharing.copyUrl(getCurrentUrl());
    if (success) {
      setToast({
        message: 'Link đã được sao chép vào clipboard!',
        type: 'success',
      });
    } else {
      setToast({
        message: 'Không thể sao chép link. Vui lòng thử lại.',
        type: 'error',
      });
    }
  };

  const shareOnWhatsApp = () => {
    if (!article) return;
    socialSharing.whatsapp({
      url: getCurrentUrl(),
      title: article.title,
      description: article.content.substring(0, 160),
    });
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;

    // Here you would typically send the comment to your backend
    console.log('Comment submitted:', comment);
    setComment('');
    // Add your comment submission logic here
  };

  if (loading) {
    return (
      <div data-component-id="src/app/tin-tuc/[slug]/page.tsx:116:6" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="116" data-component-end-line="118" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20min-h-%5B400px%5D%22%7D" className="flex items-center justify-center min-h-[400px]">
        <div data-component-id="src/app/tin-tuc/[slug]/page.tsx:117:8" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="117" data-component-end-line="117" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-spin%20rounded-full%20h-12%20w-12%20border-b-2%20border-%5B%234A306D%5D%22%7D" className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A306D]"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div data-component-id="src/app/tin-tuc/[slug]/page.tsx:124:6" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="124" data-component-end-line="129" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20min-h-%5B400px%5D%22%7D" className="flex items-center justify-center min-h-[400px]">
        <div data-component-id="src/app/tin-tuc/[slug]/page.tsx:125:8" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="125" data-component-end-line="128" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
          <h2 data-component-id="src/app/tin-tuc/[slug]/page.tsx:126:10" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="126" data-component-end-line="126" data-component-file="page.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-semibold%20text-gray-700%20mb-2%22%2C%22textContent%22%3A%22Kh%C3%B4ng%20t%C3%ACm%20th%E1%BA%A5y%20b%C3%A0i%20vi%E1%BA%BFt%22%7D" className="text-2xl font-semibold text-gray-700 mb-2">Không tìm thấy bài viết</h2>
          <p data-component-id="src/app/tin-tuc/[slug]/page.tsx:127:10" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="127" data-component-end-line="127" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-gray-500%22%2C%22textContent%22%3A%22B%C3%A0i%20vi%E1%BA%BFt%20b%E1%BA%A1n%20%C4%91ang%20t%C3%ACm%20ki%E1%BA%BFm%20kh%C3%B4ng%20t%E1%BB%93n%20t%E1%BA%A1i%20ho%E1%BA%B7c%20%C4%91%C3%A3%20b%E1%BB%8B%20x%C3%B3a.%22%7D" className="text-gray-500">Bài viết bạn đang tìm kiếm không tồn tại hoặc đã bị xóa.</p>
        </div>
      </div>
    );
  }

  return (
    <article data-component-id="src/app/tin-tuc/[slug]/page.tsx:134:4" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="134" data-component-end-line="170" data-component-file="page.tsx" data-component-name="article" data-component-content="%7B%22elementName%22%3A%22article%22%2C%22className%22%3A%22article%22%7D" className="article">
      <ArticleMetadata data-component-id="src/app/tin-tuc/[slug]/page.tsx:135:6" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="135" data-component-end-line="135" data-component-file="page.tsx" data-component-name="ArticleMetadata" data-component-content="%7B%22elementName%22%3A%22ArticleMetadata%22%7D" article={article} />
      {toast && <Toast data-component-id="src/app/tin-tuc/[slug]/page.tsx:136:16" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="136" data-component-end-line="136" data-component-file="page.tsx" data-component-name="Toast" data-component-content="%7B%22elementName%22%3A%22Toast%22%7D" message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main data-component-id="src/app/tin-tuc/[slug]/page.tsx:137:6" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="137" data-component-end-line="169" data-component-file="page.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%7D">
        <div data-component-id="src/app/tin-tuc/[slug]/page.tsx:138:8" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="138" data-component-end-line="150" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22article-main%22%7D" className="article-main">
          <div data-component-id="src/app/tin-tuc/[slug]/page.tsx:139:10" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="139" data-component-end-line="144" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22article-heading%20mb-6%22%7D" className="article-heading mb-6">
            <h1 data-component-id="src/app/tin-tuc/[slug]/page.tsx:140:12" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="140" data-component-end-line="143" data-component-file="page.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-3xl%20lg%3Atext-4xl%20font-bold%20text-gray-900%20leading-tight%22%7D"
              className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight"
              dangerouslySetInnerHTML={{ __html: article.title }}
            />
          </div>

          <div data-component-id="src/app/tin-tuc/[slug]/page.tsx:146:10" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="146" data-component-end-line="149" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22article-content%20prose%20prose-lg%20max-w-none%20mb-8%22%7D"
            className="article-content prose prose-lg max-w-none mb-8"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>

        {/* Social Sharing Section */}
        <div data-component-id="src/app/tin-tuc/[slug]/page.tsx:153:8" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="153" data-component-end-line="166" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-3%20justify-center%20mb-8%22%7D" className="flex gap-3 justify-center mb-8">
          <div data-component-id="src/app/tin-tuc/[slug]/page.tsx:154:10" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="154" data-component-end-line="165" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20group%22%7D" className="relative group">
            <button data-component-id="src/app/tin-tuc/[slug]/page.tsx:155:12" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="155" data-component-end-line="161" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22bg-gray-600%20p-3%20rounded-full%20hover%3Ascale-110%20transition-all%20duration-300%20flex%20items-center%20justify-center%20shadow-lg%20hover%3Ashadow-xl%22%7D"
              onClick={copyToClipboard}
              className="bg-gray-600 p-3 rounded-full hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl"
              title="Sao chép liên kết"
            >
              <Link data-component-id="src/app/tin-tuc/[slug]/page.tsx:160:14" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="160" data-component-end-line="160" data-component-file="page.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22w-5%20h-5%20text-white%22%7D" className="w-5 h-5 text-white" />
            </button>
            <span data-component-id="src/app/tin-tuc/[slug]/page.tsx:162:12" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="162" data-component-end-line="164" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20bottom-full%20left-1%2F2%20transform%20-translate-x-1%2F2%20mb-2%20px-2%20py-1%20bg-gray-800%20text-white%20text-xs%20rounded%20opacity-0%20group-hover%3Aopacity-100%20transition-opacity%20duration-200%20whitespace-nowrap%22%2C%22textContent%22%3A%22Sao%20ch%C3%A9p%20li%C3%AAn%20k%E1%BA%BFt%22%7D" className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
              Sao chép liên kết
            </span>
          </div>
        </div>

        <ContactForm data-component-id="src/app/tin-tuc/[slug]/page.tsx:168:8" data-component-path="src/app/tin-tuc/[slug]/page.tsx" data-component-line="168" data-component-end-line="168" data-component-file="page.tsx" data-component-name="ContactForm" data-component-content="%7B%22elementName%22%3A%22ContactForm%22%7D" />
      </main>
    </article>
  );
};

export default ArticlePage;
